import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../contexts/I18nContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Plus, ThumbsUp, ThumbsDown, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { can } from "../lib/rbac";
import { pickDefaultSchool } from "../lib/autoSelect";
import { localName, localFullName } from "../lib/localName";

export default function BehaviorPage() {
  const {  t , lang } = useI18n();
  const { user } = useAuth();
  const canEdit = can.addBehavior(user);
  const isParent = user?.role === "parent";
  const isStudent = user?.role === "student";
  const isPersonal = isParent || isStudent;

  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState("");
  const [classes, setClasses] = useState([]);
  const [classId, setClassId] = useState("");
  const [myChildren, setMyChildren] = useState([]);
  const [data, setData] = useState({ items: [], total_points: 0 });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ category: "discipline", description: "", points: 1, severity: "low" });

  // Staff: schools (auto-select first) → classes (auto-select first) → students
  useEffect(() => {
    if (!isPersonal) api.get("/schools").then(r => {
      const list = r.data || [];
      setSchools(list);
      setSchool(prev => prev || pickDefaultSchool(list, user));
    });
  }, [isPersonal]);
  useEffect(() => {
    if (isPersonal || !school) return;
    api.get(`/classes?school_id=${school}`).then(r => {
      const list = r.data || [];
      setClasses(list);
      setClassId(prev => (prev && list.some(c => c.id === prev)) ? prev : (list[0]?.id || ""));
    });
  }, [school, isPersonal]);
  useEffect(() => {
    if (isPersonal || !classId) return;
    setStudentId("");
    api.get(`/students?class_id=${classId}`).then(r => setStudents(r.data || []));
  }, [classId, isPersonal]);

  // Personal: own data
  useEffect(() => {
    if (isParent) {
      api.get("/my/children").then((r) => {
        const kids = r.data || [];
        setMyChildren(kids);
        if (kids.length && !studentId) setStudentId(kids[0].id);
      });
    } else if (isStudent) {
      api.get("/my/student-record").then((r) => {
        const s = r.data;
        if (s) { setMyChildren([s]); setStudentId(s.id); }
      });
    }
  }, [isParent, isStudent]);

  useEffect(() => { if (studentId) api.get(`/behavior?student_id=${studentId}`).then(r => setData(r.data)); }, [studentId, open]);

  const save = async () => {
    await api.post("/behavior", { ...form, student_id: studentId, points: parseInt(form.points) });
    toast.success(t("success")); setOpen(false); setForm({ category: "discipline", description: "", points: 1, severity: "low" });
  };
  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    await api.delete(`/behavior/${id}`);
    toast.success(t("success"));
    api.get(`/behavior?student_id=${studentId}`).then(r => setData(r.data));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between flex-wrap gap-3 items-center">
        <h1 className="text-3xl font-semibold">{t("behavior")}</h1>
        <div className="flex gap-3 flex-wrap">
          {isPersonal ? (
            isParent && myChildren.length > 1 ? (
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="w-72" data-testid="beh-child-select"><SelectValue placeholder={t("select_child")} /></SelectTrigger>
                <SelectContent>{myChildren.map(c => <SelectItem key={c.id} value={c.id}>{localFullName(c, lang)}</SelectItem>)}</SelectContent>
              </Select>
            ) : myChildren.length === 1 ? (
              <span className="text-sm text-muted-foreground bg-muted/40 px-3 py-2 rounded-md">
                {myChildren[0].full_name_ar || myChildren[0].full_name}
              </span>
            ) : (
              <span className="text-sm text-muted-foreground">{t("no_linked_children")}</span>
            )
          ) : (
            <>
              <Select value={school} onValueChange={setSchool}>
                <SelectTrigger className="w-52" data-testid="beh-school"><SelectValue placeholder={t("select_school")} /></SelectTrigger>
                <SelectContent>{schools.map(s => <SelectItem key={s.id} value={s.id}>{localName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={classId} onValueChange={setClassId}>
                <SelectTrigger className="w-44" data-testid="beh-class"><SelectValue placeholder={t("select_class")} /></SelectTrigger>
                <SelectContent>{classes.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger className="w-60" data-testid="beh-student"><SelectValue placeholder={t("students")} /></SelectTrigger>
                <SelectContent>{students.map(s => <SelectItem key={s.id} value={s.id}>{localFullName(s, lang)}</SelectItem>)}</SelectContent>
              </Select>
            </>
          )}
          {canEdit && <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button disabled={!studentId} data-testid="new-beh-btn"><Plus className="w-4 h-4 me-2" />{t("new_incident")}</Button></DialogTrigger>
            <DialogContent><DialogHeader><DialogTitle>{t("new_incident")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label>{t("category")}</Label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="discipline">{t("cat_discipline")}</SelectItem>
                      <SelectItem value="respect">{t("cat_respect")}</SelectItem>
                      <SelectItem value="homework">{t("cat_homework")}</SelectItem>
                      <SelectItem value="uniform">{t("cat_uniform")}</SelectItem>
                      <SelectItem value="participation">{t("cat_participation")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>{t("description")}</Label><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>{t("points")} (+/-)</Label><Input type="number" value={form.points} onChange={e => setForm({ ...form, points: e.target.value })} /></div>
                  <div><Label>{t("severity")}</Label>
                    <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}><SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">{t("sev_low")}</SelectItem>
                        <SelectItem value="med">{t("sev_med")}</SelectItem>
                        <SelectItem value="high">{t("sev_high")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button onClick={save} className="w-full" data-testid="beh-save">{t("save")}</Button>
              </div>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      {studentId && (
        <>
          <Card className="p-6 border shadow-none flex items-center justify-between">
            <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{t("total_points")}</div>
            <div className={`text-4xl font-semibold ${data.total_points >= 0 ? "text-primary" : "text-destructive"}`} data-testid="beh-total">{data.total_points}</div>
          </Card>
          <div className="space-y-2">
            {data.items.map(it => (
              <Card key={it.id} className="p-4 border shadow-none flex items-center gap-4" data-testid={`beh-${it.id}`}>
                <div className={`w-10 h-10 rounded-lg grid place-items-center ${it.points >= 0 ? "bg-primary/10 text-primary" : "bg-accent/10 text-accent"}`}>
                  {it.points >= 0 ? <ThumbsUp className="w-5 h-5" /> : <ThumbsDown className="w-5 h-5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium truncate">{it.description}</div>
                  <div className="text-xs text-muted-foreground font-mono">{t(`cat_${it.category}`)} • {t(`sev_${it.severity}`)} • {it.at?.slice(0, 10)}</div>
                </div>
                <div className={`font-mono font-semibold ${it.points >= 0 ? "text-primary" : "text-destructive"}`}>{it.points > 0 ? `+${it.points}` : it.points}</div>
                <button onClick={() => remove(it.id)} className="text-muted-foreground hover:text-destructive smooth p-1" data-testid={`beh-del-${it.id}`} aria-label="delete" hidden={!canEdit} style={!canEdit ? { display: "none" } : undefined}><Trash2 className="w-4 h-4" /></button>
              </Card>
            ))}
            {data.items.length === 0 && <Card className="p-8 text-center text-muted-foreground border-dashed">{t("no_data")}</Card>}
          </div>
        </>
      )}
    </div>
  );
}
